import * as React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { UserToken } from '../../../../../amorg-common';
import { ProtectedPageContext } from '../../../../_contexts/ProtectedPageContext';
import { tablet } from '../../../../../designSystem/BreakPoints';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { Divider } from '../../../../../designSystem/Divider';
import { Icon } from '../../../../../designSystem/icons/Icons';
import { Strong } from '../../../../../designSystem/typography/Body';
import { MenuItem, MenuItemGroup, MenuItemGroupProps, MenuItemProps } from './MenuItem';



type MenuItemDefinition = 
    MenuItemProps & {
        type:'menu-item',
        show:boolean
    };

type MenuItemGroupDefinition =
    MenuItemGroupProps & {
        type:'menu-item-group',
        subItems: MenuItemDefinition[]
    };

type MenuItemDividerDefinition = {
    type:'menu-divider'
}

type NavigationItemDefinition = MenuItemDefinition | MenuItemGroupDefinition | MenuItemDividerDefinition;

type LeftMenuNavigationProps = {
    activeUser: UserToken | null,
    selectedMenuItemKey?: string,
    items: NavigationItemDefinition[],
};

export const LeftMenuNavigation = ({ activeUser, selectedMenuItemKey, items }: LeftMenuNavigationProps): JSX.Element => 
{
    const [isMobileNavigationActive, setIsMobileNavigationActive] = React.useState(false);
    const context = React.useContext(ProtectedPageContext);

    useEffect(() => {
        // Handle scroll lock for mobile navigation
        if (isMobileNavigationActive) {
            document.body.classList.add('no-scroll');
            document.documentElement.style.overflowX = 'hidden';
            document.body.style.overflowX = 'hidden';
        } else {
            document.body.classList.remove('no-scroll');
            document.documentElement.style.overflowX = '';
            document.body.style.overflowX = '';
        }

        return () => {
            document.body.classList.remove('no-scroll');
            document.documentElement.style.overflowX = '';
            document.body.style.overflowX = '';
        };
    }, [isMobileNavigationActive]);

    const handleMenuToggle = () =>
    {
        setIsMobileNavigationActive(!isMobileNavigationActive);
    };

    const applyPartnerLogo = () =>
    {

        if (context.user.partner === 'Crecea')
        {
            return <img src="/images/partner/crecea/crecea_logo_white.svg" />;
        }

        if(context.user.partner === 'Joblife')
        {
            return <img width="200" src="/images/partner/creceajoblife/CreceaJoblife_white.svg" />;
        }

    };

    return (
        <OuterWrapper isMobileNavigationActive={isMobileNavigationActive}>

            <MenuWrapper className='MenuWrapper'>
                <Flex flexDirection="row" alignItems='center' justifyContent='space-between'>

                    <NavigationTrigger onClick={handleMenuToggle}>
                        <Icon name="navigation_menu" size="medium" />
                    </NavigationTrigger>
                </Flex>
                <MenuContent isMobileNavigationActive={isMobileNavigationActive}>
                        {applyPartnerLogo()}
                        <Navigation className="_navigation">
                            <List>
                                <>
                                    {activeUser &&
                                        <>
                                            {items.map((item, index) => {
                                                if(item.type === 'menu-item')
                                                {
                                                    if(item.show)
                                                    {
                                                        return <MenuItem key={index} selectedMenuItemKey={selectedMenuItemKey} {...item} />
                                                    }
                                                    else {
                                                        return null;
                                                    }
                                                }
                                                else if(item.type === 'menu-item-group')
                                                {
                                                    if(item.subItems.some(subItem => subItem.show))
                                                    {
                                                        return (
                                                            <MenuItemGroup key={index} {...item}>
                                                                {item.subItems.map((subItem, subIndex) => {
                                                                    if(subItem.type === 'menu-item' && subItem.show)
                                                                    {
                                                                        return <MenuItem key={subIndex} selectedMenuItemKey={selectedMenuItemKey} {...subItem} />
                                                                    }
                                                                    return null;
                                                                })}
                                                            </MenuItemGroup>
                                                        )
                                                    }
                                                }
                                                else if(item.type === 'menu-divider')
                                                {
                                                    const hasItemsBefore = index > 0 
                                                        && items.slice(0, index).some(i => 
                                                            (i.type === 'menu-item' && i.show) || (i.type === 'menu-item-group' && i.subItems.some(subItem => subItem.show)));

                                                    const hasItemsAfter = index < items.length - 1 
                                                        && items.slice(index + 1).some(i => 
                                                            (i.type === 'menu-item' && i.show) || (i.type === 'menu-item-group' && i.subItems.some(subItem => subItem.show)));

                                                    if(hasItemsBefore && hasItemsAfter)
                                                    {
                                                        return <Divider key={index} color={context.colors.mainNavigation.divider} />
                                                    }
                                                }
                                            })}
                                        </>
                                    }
                                </>
                            </List>
                        </Navigation>
                    </MenuContent>
            </MenuWrapper>
            <MenuFooter isMobileNavigationActive={isMobileNavigationActive}>
                <Flex flexDirection="row" alignItems='center' marginTop={8}>
                    {(context.user.partner) &&
                        <PoweredBy>Powered by</PoweredBy>
                    }
                    <LogoContainer>
                        <img src="/images/amorg_logo_blue.svg" />
                    </LogoContainer>
                    <Strong>AMORG</Strong>
                </Flex>
            </MenuFooter>
        </OuterWrapper>
    );
};



const NavigationTrigger = styled.div`

    display:none;

    @media (max-width: ${tablet}px) {
        display:block;

        & svg{
            fill: white;
            
        }
    }

`;

const OuterWrapper = styled.div<{ isMobileNavigationActive: boolean; }>`  
    grid-area: navigation;
    font-family: "Noto Sans", sans-serif;
    color: white;
    background-color: ${props => props.theme.colors.mainNavigation.background};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    overflow-y: auto;
    
    /* Desktop styles */
    width: 320px;
    height: 100%;
    padding: 24px 0;
    position: fixed;

    @media (max-width: ${tablet}px) {
        /* Mobile styles */
        grid-area: topbar;
        width: ${props => props.isMobileNavigationActive ? '100%' : '56px'};
        max-width: 100vw;
        position: fixed;
        top: 0;
        left: 0;
        right: auto;
        padding: 16px 0;
        overflow-x: hidden;
        overflow-y: auto;
        height: ${props => props.isMobileNavigationActive ? '100vh' : '56px'};
        z-index: ${props => props.isMobileNavigationActive ? '1000' : '101'};
    }
`;

const MenuWrapper = styled.div``;

const MenuFooter = styled.div<{ isMobileNavigationActive: boolean }>`
    @media (max-width: ${tablet}px) {
        display: ${props => props.isMobileNavigationActive ? 'block' : 'none'};
    }
`;

const MenuContent = styled.div<{ isMobileNavigationActive: boolean }>`
    @media (max-width: ${tablet}px) {
        display: ${props => props.isMobileNavigationActive ? 'block' : 'none'};
    }
`;

const LogoContainer = styled.div`
    height: 32px;
    width: 32px;
    margin-left: 8px;
    margin-right: 8px;
`;

const Navigation = styled.nav`
    margin:12px auto;   
`;

const List = styled.ul`
    list-style: none;
    margin:0px;
    padding:0px;
`;

const PoweredBy = styled.div`
    font-size: 12px;
    color: white;
    font-weight: 700;
`;